<template>
  <div>
    <label class="label" :for="inputId">Capacity plan (.xlsx)</label>

    <div
      class="relative mt-1 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors"
      :class="zoneClass"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        :id="inputId"
        ref="input"
        type="file"
        accept=".xlsx"
        :disabled="disabled"
        class="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        :aria-describedby="describedById"
        @change="onChange"
        @focus="focused = true"
        @blur="focused = false"
      />

      <template v-if="modelValue">
        <p class="m-0 text-sm font-medium text-gray-900">{{ modelValue.name }}</p>
        <p :id="stagedId" class="m-0 mt-0.5 text-xs text-gray-500">{{ formatSize(modelValue.size) }} · ready to parse</p>
        <button type="button" class="relative z-10 mt-3 text-xs font-medium text-blue-700 underline" :disabled="disabled" @click.stop="clear">Choose a different file</button>
      </template>

      <template v-else>
        <p class="m-0 text-sm font-medium text-gray-700">
          <span class="text-blue-700 underline">Choose a file</span>
          or drag it here
        </p>
        <p :id="hintId" class="m-0 mt-1 text-xs text-gray-500">Excel workbook (.xlsx), up to {{ maxMb }} MB</p>
      </template>
    </div>

    <p v-if="tooLarge" role="alert" class="mt-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      This file is {{ formatSize(modelValue!.size) }} — larger than the {{ maxMb }} MB limit. Export a smaller plan and try again.
    </p>
    <p v-else-if="wrongType" role="alert" class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      That file isn't an .xlsx workbook. Export the capacity plan as Excel and drop it here.
    </p>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

  const BYTES_PER_MB = 1024 * 1024;
  const SIZE_DECIMALS = 1;

  const props = withDefaults(defineProps<{ modelValue: File | null; maxBytes: number; disabled?: boolean }>(), { disabled: false });
  const emit = defineEmits<{ 'update:modelValue': [File | null] }>();

  const inputId = 'capacity-plan-file';
  const hintId = `${inputId}-hint`;
  const stagedId = `${inputId}-staged`;
  const input = ref<HTMLInputElement | null>(null);
  const dragging = ref(false);
  const wrongType = ref(false);
  const focused = ref(false);
  // Depth counter, not a boolean: dragging over a child element fires dragleave on the parent,
  // which would otherwise clear the highlight while the pointer is still inside the zone.
  const dragDepth = ref(0);

  const maxMb = computed(() => Math.round(props.maxBytes / BYTES_PER_MB));
  const tooLarge = computed(() => (props.modelValue?.size ?? 0) > props.maxBytes);
  // Whichever paragraph is actually rendered for the current state — must always point at a
  // live element, or screen readers lose the description once a file is staged.
  const describedById = computed(() => (props.modelValue ? stagedId : hintId));

  const zoneClass = computed(() => {
    const base = (() => {
      if (props.disabled) return 'border-gray-200 bg-gray-50 opacity-60';
      if (dragging.value) return 'border-blue-500 bg-blue-50';
      if (tooLarge.value) return 'border-red-300 bg-red-50';
      if (props.modelValue) return 'border-green-300 bg-green-50';
      return 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50';
    })();
    // Opacity on the real input suppresses its native focus outline, so the visible
    // focus indicator has to be painted on the zone itself instead.
    return focused.value ? `${base} ring-2 ring-blue-500 ring-offset-2` : base;
  });

  function isXlsx(file: File): boolean {
    return file.name.toLowerCase().endsWith('.xlsx');
  }

  function resetNativeInputValue(): void {
    // The native input keeps its value after a programmatic clear, so re-picking the same
    // file would fire no change event.
    if (input.value) input.value.value = '';
  }

  function accept(file: File | null): void {
    if (file && !isXlsx(file)) {
      wrongType.value = true;
      resetNativeInputValue();
      emit('update:modelValue', null);
      return;
    }
    wrongType.value = false;
    emit('update:modelValue', file);
  }

  function onChange(event: Event): void {
    accept((event.target as HTMLInputElement).files?.[0] ?? null);
  }

  function resetDragState(): void {
    dragDepth.value = 0;
    dragging.value = false;
  }

  function onDragEnter(): void {
    if (props.disabled) return;
    dragDepth.value += 1;
    dragging.value = true;
  }

  function onDragOver(): void {
    if (props.disabled) return;
    dragging.value = true;
  }

  function onDragLeave(): void {
    dragDepth.value -= 1;
    if (dragDepth.value <= 0) resetDragState();
  }

  function onDrop(event: DragEvent): void {
    resetDragState();
    if (props.disabled) return;
    accept(event.dataTransfer?.files?.[0] ?? null);
    resetNativeInputValue();
  }

  // The element's own dragleave already covers the ordinary exit and an Escape cancellation
  // (the spec fires dragleave at the current target when a drag is cancelled). These window
  // listeners are the fallback for a drag that leaves the document entirely, or is dropped
  // elsewhere on the page: dragleave only when relatedTarget is null (pointer left the
  // document, not just moved to another element), and drop/dragover unconditionally. Browsers
  // only suppress their default "navigate to the dropped file" behaviour when dragover is
  // cancelled, so it must be prevented at the window level too, not just on the dashed zone.
  function onWindowDragLeave(event: DragEvent): void {
    if (event.relatedTarget === null) resetDragState();
  }

  function onWindowDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  function onWindowDrop(event: DragEvent): void {
    event.preventDefault();
    resetDragState();
  }

  function clear(): void {
    resetNativeInputValue();
    wrongType.value = false;
    emit('update:modelValue', null);
  }

  function formatSize(bytes: number): string {
    return `${(bytes / BYTES_PER_MB).toFixed(SIZE_DECIMALS)} MB`;
  }

  onMounted(() => {
    window.addEventListener('dragleave', onWindowDragLeave);
    window.addEventListener('dragover', onWindowDragOver);
    window.addEventListener('drop', onWindowDrop);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('dragleave', onWindowDragLeave);
    window.removeEventListener('dragover', onWindowDragOver);
    window.removeEventListener('drop', onWindowDrop);
  });
</script>
